import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '@/context/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { ChevronLeft, Send, MessageSquare, User } from 'lucide-react';
import VoteBox from '@/components/VoteBox';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CommentsPage = () => {
  const { language, t } = useLanguage();
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: '', message: '' });

  useEffect(() => {
    fetchComments();
  }, []);

  const fetchComments = async () => {
    try {
      const res = await axios.get(`${API}/comments`);
      setComments(res.data);
    } catch (error) {
      console.error('Failed to fetch comments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.message.trim()) return;
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/comments`, form);
      toast.success(language === 'de' ? 'Kommentar gesendet!' : 'Comment posted!');
      setForm({ name: '', message: '' });
      fetchComments();
    } catch (error) {
      toast.error(language === 'de' ? 'Fehler beim Senden' : 'Failed to post');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(language === 'de' ? 'de-DE' : 'en-US', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-void hero-bg grid-bg">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass-panel border-b border-cyan-electric/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link 
              to="/" 
              className="flex items-center text-slate-400 hover:text-cyan-electric transition-colors"
              data-testid="back-to-home"
            >
              <ChevronLeft className="w-5 h-5 mr-1" />
              <span className="font-rajdhani uppercase tracking-wider text-sm">
                {language === 'de' ? 'Zurück zu Events' : 'Back to Events'}
              </span>
            </Link>
            
            <div className="flex items-center space-x-3">
              <MessageSquare className="w-5 h-5 text-cyan-electric" />
              <h1 className="font-rajdhani text-xl font-bold uppercase tracking-widest text-white">
                {language === 'de' ? 'Kommentare' : 'Comments'}
              </h1>
            </div>
            
            <div className="w-32"></div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="pt-24 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          {/* Comment Form */}
          <div className="glass-panel holo-border rounded-lg p-6 mb-8">
            <h2 className="font-rajdhani text-lg uppercase tracking-wider text-cyan-electric mb-4">
              {language === 'de' ? 'Schreibe einen Kommentar' : 'Write a Comment'}
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="comment-form">
              <div>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <Input
                    value={form.name}
                    onChange={(e) => setForm({...form, name: e.target.value})}
                    className="pl-10 input-command font-barlow"
                    placeholder={language === 'de' ? 'Dein Name...' : 'Your name...'}
                    required
                    maxLength={50}
                    data-testid="comment-name"
                  />
                </div>
              </div>
              
              <div>
                <Textarea
                  value={form.message}
                  onChange={(e) => setForm({...form, message: e.target.value})}
                  className="input-command font-barlow min-h-[100px]"
                  placeholder={language === 'de' ? 'Deine Nachricht...' : 'Your message...'}
                  required
                  maxLength={500}
                  data-testid="comment-message"
                />
              </div>
              
              <Button
                type="submit"
                disabled={submitting}
                className="btn-power bg-cyan-electric/10 border border-cyan-electric/50 text-cyan-electric hover:bg-cyan-electric/20"
                data-testid="comment-submit"
              >
                <Send className="w-4 h-4 mr-2" />
                {submitting 
                  ? (language === 'de' ? 'Sende...' : 'Sending...') 
                  : (language === 'de' ? 'Absenden' : 'Send')
                }
              </Button>
            </form>
          </div>

          {/* Comments List */}
          <div className="space-y-4">
            <h2 className="font-rajdhani text-lg uppercase tracking-wider text-cyan-electric/70">
              {language === 'de' ? `${comments.length} Kommentare` : `${comments.length} Comments`}
            </h2>
            
            {loading ? (
              <div className="text-center text-cyan-electric font-mono animate-pulse py-8">
                {language === 'de' ? 'Lade Kommentare...' : 'Loading comments...'}
              </div>
            ) : comments.length === 0 ? (
              <div className="glass-panel rounded-lg p-8 text-center">
                <MessageSquare className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-500 font-barlow">
                  {language === 'de' 
                    ? 'Noch keine Kommentare. Sei der Erste!' 
                    : 'No comments yet. Be the first!'}
                </p>
              </div>
            ) : (
              comments.map((comment) => (
                <div 
                  key={comment.id} 
                  className="glass-panel rounded-lg p-4 border-l-2 border-cyan-electric/30"
                  data-testid={`comment-${comment.id}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-rajdhani font-semibold text-white">
                      {comment.name}
                    </span>
                    <span className="font-mono text-xs text-slate-500">
                      {formatDate(comment.created_at)}
                    </span>
                  </div>
                  <p className="font-barlow text-slate-300 leading-relaxed">
                    {comment.message}
                  </p>
                </div>
              ))
            )}
          </div>
        </div>
      </main>

      {/* Footer with Vote Box */}
      <footer className="glass-panel border-t border-cyan-electric/20 py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <Link 
              to="/"
              className="text-cyan-electric hover:text-cyan-electric/80 font-rajdhani uppercase tracking-wider text-sm transition-colors"
            >
              ← {language === 'de' ? 'Zurück zur Startseite' : 'Back to Home'}
            </Link>
            <VoteBox language={language} />
          </div>
        </div>
      </footer>
    </div>
  );
};

export default CommentsPage;
